export interface CreatorInviteFields {
  inviteCreatorName: string;
  inviteCreatorEmail: string;
  inviteCreatorPhone: string;
  inviteCreatorCountry: string;
  inviteCreatorIsAgency: boolean;
  inviteCreatorEnableNotifications: boolean;
}

/**
 * Generates initial form values for creator invitation fields with a configurable prefix.
 *
 * @param namePrefix - The prefix to use for field names (default: "invite")
 * @returns Object containing initial values for all creator invitation fields
 *
 * @example
 * ```typescript
 * // Basic usage
 * const initialValues = getCreatorInviteInitialValues();
 * // Result: { inviteCreatorName: "", inviteCreatorEmail: "", ... }
 *
 * // With custom prefix
 * const customValues = getCreatorInviteInitialValues("new");
 * // Result: { newCreatorName: "", newCreatorEmail: "", ... }
 * ```
 */
export const getCreatorInviteInitialValues = (namePrefix = "invite") => ({
  [`${namePrefix}CreatorName`]: "",
  [`${namePrefix}CreatorEmail`]: "",
  [`${namePrefix}CreatorPhone`]: "",
  [`${namePrefix}CreatorCountry`]: "",
  [`${namePrefix}CreatorIsAgency`]: "",
  [`${namePrefix}CreatorEnableNotifications`]: true,
});

/**
 * Extracts creator data from form values for API submission.
 * Transforms form field names back to the expected API format.
 *
 * @param values - The form values object containing creator fields
 * @param namePrefix - The prefix used for field names (default: "invite")
 * @returns Object with creator data ready for API submission
 *
 * @example
 * ```typescript
 * const formValues = {
 *   inviteCreatorName: "John Doe",
 *   inviteCreatorEmail: "john@example.com",
 *   inviteCreatorCountry: "US",
 *   // ... other form fields
 * };
 *
 * const creatorData = extractCreatorDataFromFormValues(formValues);
 * // Result: { name: "John Doe", email: "john@example.com", country: "US", ... }
 *
 * // Use in API call
 * await createAssignment({
 *   ...assignmentData,
 *   ...creatorData,
 * });
 * ```
 */
export const extractCreatorDataFromFormValues = (
  values: any,
  namePrefix = "invite",
) => {
  const prefix = `${namePrefix}Creator`;

  return {
    name: values[`${prefix}Name`],
    email: values[`${prefix}Email`],
    phone: values[`${prefix}Phone`] || undefined,
    country: values[`${prefix}Country`],
    isAgency: values[`${prefix}IsAgency`],
    enableNotifications: values[`${prefix}EnableNotifications`],
  };
};

/**
 * Clears all creator invitation fields in a Formik form.
 * Resets values to initial state, clears touched and error states.
 *
 * @param setFieldValue - Formik's setFieldValue function
 * @param setFieldTouched - Formik's setFieldTouched function
 * @param setFieldError - Formik's setFieldError function
 * @param namePrefix - The prefix used for field names (default: "invite")
 *
 * @example
 * ```typescript
 * // In a Formik component
 * const handleClearCreatorFields = () => {
 *   clearCreatorInviteFields(
 *     setFieldValue,
 *     setFieldTouched,
 *     setFieldError
 *   );
 * };
 *
 * // With custom prefix
 * const handleClearCustomFields = () => {
 *   clearCreatorInviteFields(
 *     setFieldValue,
 *     setFieldTouched,
 *     setFieldError,
 *     "custom"
 *   );
 * };
 * ```
 */
export const clearCreatorInviteFields = (
  setFieldValue: (field: string, value: any) => void,
  setFieldTouched: (field: string, touched: boolean) => void,
  setFieldError: (field: string, error: string) => void,
  namePrefix = "invite",
) => {
  const fields = [
    "Name",
    "Email",
    "Phone",
    "Country",
    "IsAgency",
    "EnableNotifications",
  ];

  const initialValues = getCreatorInviteInitialValues(namePrefix);

  fields.forEach((field) => {
    const fieldName = `${namePrefix}Creator${field}`;
    const initialValue = initialValues[fieldName as keyof typeof initialValues];
    setFieldValue(fieldName, initialValue);
    setFieldTouched(fieldName, false);
    setFieldError(fieldName, "");
  });
};
